import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import countryList from "react-select-country-list";
import { gr } from "../tokens";
import icons from "../icons";
import dialCodes, { primaryCountryForCode } from "../data/dialCodes";
import GrIcon from "./GrIcon";
import GrSearch from "./GrSearch";
import GrTextField from "./GrTextField";
import { grPopoverPaperSx } from "./GrMenu";

const DEFAULT_COUNTRY = "IE";

// Flag as emoji from the ISO code (no images to download). Windows shows the two letters instead.
export const flagEmoji = (iso = "") =>
  /^[A-Z]{2}$/.test(iso)
    ? String.fromCodePoint(...[...iso].map((c) => 127397 + c.charCodeAt(0)))
    : "";

const validCountry = (iso) => (iso && dialCodes[iso] ? iso : DEFAULT_COUNTRY);

// Splits a stored phone value. "+353…" / "00353…" → country from the longest matching code.
// Anything else is an old number saved without a code: it is kept exactly as it is (legacy).
export function splitPhone(value, fallbackCountry) {
  const raw = String(value ?? "").trim();
  const fallback = validCountry(fallbackCountry);
  if (/^(\+|00)/.test(raw)) {
    const digits = raw.replace(/^(\+|00)/, "").replace(/\D/g, "");
    for (let len = 4; len >= 1; len -= 1) {
      const code = digits.slice(0, len);
      const matches = Object.keys(dialCodes).filter(
        (iso) => dialCodes[iso] === code
      );
      if (matches.length) {
        const iso = matches.includes(fallback)
          ? fallback
          : primaryCountryForCode[code] || matches[0];
        return { country: iso, national: digits.slice(len), legacy: false };
      }
    }
  }
  return { country: fallback, national: raw, legacy: Boolean(raw) };
}

// Joins country + typed number into one international value: "+<code><digits>".
// The national trunk 0 is dropped (087… → +35387…), except for Italy where it is part of the number.
export function joinPhone(country, national) {
  const code = dialCodes[country];
  let digits = String(national ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (code !== "39") digits = digits.replace(/^0/, "");
  return `+${code}${digits}`;
}

// Phone input with a country code picker (flag + code, searchable).
// Value in and out is one string; onChange gets { target: { name, value } } like a normal input.
// Old numbers saved without a code are shown as they are and sent back unchanged until the member edits them.
export default function GrPhoneField({
  name,
  value,
  onChange,
  defaultCountry,
  label = "Phone",
  id,
  sx,
  ...rest
}) {
  const countries = useMemo(
    () =>
      countryList()
        .getData()
        .map((c) => ({
          iso: c.value,
          name: c.label,
          code: dialCodes[c.value],
        })),
    []
  );
  const [state, setState] = useState(() => splitPhone(value, defaultCountry));
  const lastEmitted = useRef(value);
  const [anchor, setAnchor] = useState(null);
  const [query, setQuery] = useState("");
  const listRef = useRef(null);

  // Re-read the value when it changes from outside (e.g. profile loaded from the API).
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setState(splitPhone(value, defaultCountry));
    }
  }, [value, defaultCountry]);

  // Old numbers: follow the member's Country until they choose a code themselves.
  useEffect(() => {
    setState((s) =>
      s.legacy || !s.national
        ? { ...s, country: validCountry(defaultCountry) }
        : s
    );
  }, [defaultCountry]);

  const emit = (next) => {
    const out = joinPhone(next.country, next.national);
    lastEmitted.current = out;
    onChange?.({ target: { name, value: out } });
  };

  const handleNumber = (e) => {
    const next = { ...state, national: e.target.value, legacy: false };
    setState(next);
    emit(next);
  };

  const pickCountry = (iso) => {
    // Choosing a code for a typed or old number counts as an edit; an empty field stays empty.
    const next = {
      ...state,
      country: iso,
      legacy: state.national ? false : state.legacy,
    };
    setState(next);
    if (state.national) emit(next);
    setAnchor(null);
    setQuery("");
  };

  const q = query.trim().toLowerCase().replace(/^\+/, "");
  const filtered = q
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.code.startsWith(q) ||
          c.iso.toLowerCase() === q
      )
    : countries;
  const current = countries.find((c) => c.iso === state.country);
  const pickerId = `${id || name || "phone"}-country`;

  return (
    <>
      <GrTextField
        id={id}
        name={name}
        label={label}
        type="tel"
        autoComplete="tel-national"
        value={state.national}
        onChange={handleNumber}
        sx={sx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: gr.space[2] }}>
              <ButtonBase
                id={pickerId}
                aria-label={`Country code: ${current?.name || ""} +${
                  current?.code || ""
                }`}
                aria-haspopup="listbox"
                aria-expanded={Boolean(anchor)}
                onClick={(e) =>
                  setAnchor(e.currentTarget.closest(".MuiInputBase-root"))
                }
                sx={{
                  gap: gr.space[1],
                  height: gr.control.sm,
                  px: gr.space[2],
                  ml: `-${gr.space[1]}`,
                  borderRadius: gr.radius.chip,
                  fontFamily: gr.font.family,
                  fontSize: gr.font.size.md,
                  color: gr.color.ink,
                  borderRight: `1px solid ${gr.color.line}`,
                  "&:hover": { background: gr.color.tint },
                  "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
                }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{ fontSize: gr.font.size.lg, lineHeight: 1 }}
                >
                  {flagEmoji(state.country)}
                </Box>
                <span>+{current?.code}</span>
                <GrIcon icon={icons.chevronDown} width={16} height={16} />
              </ButtonBase>
            </InputAdornment>
          ),
        }}
        {...rest}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setQuery("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            ...grPopoverPaperSx,
            mt: gr.space[1],
            width: 320,
            maxWidth: "calc(100vw - 32px)",
          },
        }}
      >
        <Box
          sx={{ p: gr.space[2], borderBottom: `1px solid ${gr.color.line2}` }}
        >
          <GrSearch
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery("")}
            placeholder="Search country or code"
            label="Search country or code"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                listRef.current?.querySelector("li")?.focus();
              }
              if (e.key === "Enter" && filtered[0]) {
                e.preventDefault();
                pickCountry(filtered[0].iso);
              }
            }}
          />
        </Box>
        <MenuList
          ref={listRef}
          role="listbox"
          aria-labelledby={pickerId}
          sx={{ maxHeight: 280, overflowY: "auto", py: gr.space[1] }}
        >
          {filtered.map((c) => (
            <MenuItem
              key={c.iso}
              role="option"
              selected={c.iso === state.country}
              aria-selected={c.iso === state.country}
              onClick={() => pickCountry(c.iso)}
              sx={{
                gap: gr.space[3],
                minHeight: 40,
                fontFamily: gr.font.family,
                fontSize: gr.font.size.md,
                color: gr.color.ink,
                "&.Mui-selected, &.Mui-selected:hover": {
                  background: gr.color.brandSoft,
                },
              }}
            >
              <Box
                component="span"
                aria-hidden
                sx={{ fontSize: gr.font.size.lg, lineHeight: 1 }}
              >
                {flagEmoji(c.iso)}
              </Box>
              <Box
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {c.name}
              </Box>
              <Box component="span" sx={{ color: gr.color.muted }}>
                +{c.code}
              </Box>
            </MenuItem>
          ))}
          {!filtered.length ? (
            <Box
              sx={{
                px: gr.space[4],
                py: gr.space[3],
                fontSize: gr.font.size.sm,
                color: gr.color.muted,
              }}
            >
              No country found
            </Box>
          ) : null}
        </MenuList>
      </Popover>
    </>
  );
}

GrPhoneField.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  defaultCountry: PropTypes.string,
  label: PropTypes.string,
  id: PropTypes.string,
  sx: PropTypes.object,
};
